/* eslint-disable spire/export-styles */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Hidden from "@insite/mobius/Hidden";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

/* Poolcorp Custom Code */
import { StyledClickable } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import { siteWidthStyles } from "@store/theme/common/mixins";
import { COMMON, MENU, TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import isTheme from "@store/utilities/isTheme";

export const SECONDARY_NAVIGATION_HEIGHT = 45;
/* Poolcorp Custom Code */

const enum fields {
    customCSS = "customCSS",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Navigation = styled.div`
    /* Poolcorp Custom Code - Start */
    color: ${getColor("common.accentContrast")};
    /* Poolcorp Custom Code - End */
    font-size: 15px;
    background-color: ${getColor("common.accent")};
    padding: 9px 45px 4px 45px;
    text-transform: uppercase;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;
    margin-left: 30px;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    showInventoryLink?: InjectableCss;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingLink?: InjectableCss;
    showPricingToggle?: ShowPricingToggleStyles;
    currencyLink?: InjectableCss;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
    // Poolcorp Custom Code
    container?: InjectableCss;
    leftSide?: InjectableCss;
    rightSide?: InjectableCss;
}

/* Poolcorp Custom Code */
const sideWrapperStyles = css`
    display: flex;
    align-items: center;
    height: 100%;

    > div {
        /* -- Zone -- */
        display: inherit;
        align-items: inherit;
        height: 100%;

        > a,
        > div,
        > div > a,
        > div > a > span {
            /* -- Link, Image, HeaderLinkList -- */
            display: inherit;
            align-items: inherit;
            height: 100%;
        }

        .img-wrapper,
        .img-link {
            display: inherit;
            align-items: inherit;
            height: 100%;

            a,
            span {
                display: inherit;
                align-items: inherit;
                height: 100%;
            }
        }
    }
`;

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    container: {
        css: css`
            background-image: ${getColor("header.top")};
            color: ${getColor("header.topContrast")};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 1.2;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [46, null, null, null, 50], properties: ["height"], unit: "px" },
                        { values: [13, null, null, null, 14], properties: ["font-size"], unit: "px" },
                    ],
                    mediaOptions.min,
                )}

            ${StyledClickable} {
                padding: 0 ${MENU.topNavItemSpacing};
                outline-offset: -${getProp("theme.focus.width")};
                color: ${getColor("header.topContrast")};

                &:hover {
                    color: ${getColor("header.topContrast")};
                    text-decoration: underline;
                }
            }

            [data-contentid] & {
                height: auto;
            }
        `,
    },
    wrapper: {
        css: css`
            ${siteWidthStyles(COMMON.spacing.side.map(x => (x ? `calc(${x} - ${MENU.topNavItemSpacing})` : null)))}
            display: flex;
            justify-content: space-between;
            height: 100%;
        `,
    },
    leftSide: {
        css: css`
            ${sideWrapperStyles}
            > div > div {
                /* -- Zone > Image -- */
                padding: 0 ${MENU.topNavItemSpacing};
                margin: 0;
                border-right: 1px solid rgba(255, 255, 255, 0.25);

                ${StyledClickable} {
                    margin: 0 -${MENU.topNavItemSpacing};
                }

                img {
                    width: auto;
                    max-height: 44%;
                }

                [data-contentid] & {
                    padding: 0;

                    img {
                        display: block;
                        max-width: 100%;
                        max-height: 25px;
                    }
                }
            }
        `,
    },
    rightSide: {
        css: css`
            ${sideWrapperStyles}

            [data-widget] {
                min-width: 100px;
            }
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}`;

const SecondaryNavigation: FC<Props> = ({
    id,
    enableWarehousePickup,
    displayPricingAndInventory,
    currentMode,
    fields,
}) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftSide}>
                    <Zone zoneName="Logos" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.rightSide}>
                    <Zone zoneName="Links" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};
/* Poolcorp Custom Code */

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            /* Poolcorp removed - customCSS */
            /* Poolcorp removed - showPricingToggle */
            /* Poolcorp removed - showInventoryToggle */
        ],
    },
};

export default secondaryNavigation;
